import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ApiResponse, Metadata } from '../../../api/api-types/api-response.api';
import { Lek, LekSponsor } from '../../../api/app-types/lek.app';
import { LEK_PROVIDER } from '../../../api/services/lek.service';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { LocalStorageService } from '../../storage/local-storage.service';

const resolveInitialPageSize = (
	storage: LocalStorageService,
	auth: MsalService | null,
	preferenceKey: string,
	defaultPageSize: number,
	pageSizeOptions: number[],
) => {
	const userId = storage.resolveCurrentUserId(auth);
	if (!userId) return defaultPageSize;

	const saved = storage.getPageSize(userId, preferenceKey);
	return saved && pageSizeOptions.includes(saved) ? saved : defaultPageSize;
};

export const leksForAssignmentResolver: ResolveFn<PaginatedResponse<Lek>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(LEK_PROVIDER);
	const env = inject(ENVIRONMENT);
	const storage = inject(LocalStorageService);
	const auth = inject(MsalService, { optional: true });
	const assignmentId = route.paramMap.get('assignmentId');
	const initialRange = resolveInitialPageSize(
		storage,
		auth,
		'assignment-view',
		env.defaultPageSize,
		env.pageSizeOptions,
	);
	const res = await api.getAllByAssignment(assignmentId!, initialRange, 0);
	return res;
};

export const lekSponsorResolver: ResolveFn<PaginatedResponse<LekSponsor>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(LEK_PROVIDER);
	const env = inject(ENVIRONMENT);
	const res = await api.getLekSponsors(env.pagination.range, env.pagination.offset);
	return res;
};

export const lekResolver: ResolveFn<ApiResponse<Lek, Metadata>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(LEK_PROVIDER);
	const lekId = route.paramMap.get('lekId');
	const res = await api.single(lekId!);
	return res;
};
